// The Validate HTTP client (Slice 5, Flows 1-2 only; packages/shared/src/http/validate.ts;
// docs/phase-0/slice5-ux-flows.md Flows 1-2 - Talk-to-Glim, the verdict pack, the evidence
// drawer, the hardened context upload). Every response parses through the shared Zod
// schema apps/api validates against (app-engineering §2 "parse, don't validate") - never a
// re-derived client-side shape for a landed contract.
//
// ONLY the canonical routes on `VALIDATE_ROUTES` (packages/shared/src/http/validate.ts:
// 31-40) are wired here - no invented backend endpoint: verdict (POST start / GET latest /
// GET by id), evidence (GET per-claim), uploads (POST, D77 hardened gate).
// `brandDirections*`/`mockups*` are also on VALIDATE_ROUTES but belong to Flow 3 (out of
// this lane's Wave-3 dispatch scope: "Flows 1-2 only") - not wired here, left for whichever
// lane builds Brand.
//
// Talk-to-Glim's chat intake and the three-path chooser's "build anyway"/"drop" outcomes
// have NO backing route anywhere in packages/shared/src/http/*.ts (confirmed by grep for
// "validate/chat" and for a build-anyway/drop path - no match), and this lane does not
// invent one: the chat composer collects the founder's brief entirely client-side and
// sends it as the `POST verdict` request body (the one canonical write this flow needs);
// "build anyway" and "drop" are client-side navigation/confirm actions only (see
// `VerdictPackView.tsx`'s own doc comment) - any server-side verdict write or app-row
// cleanup they eventually need is a different lane's contract to land, not this file's.
import { z } from "zod";
import { EvidencePointer, ValidateUploadResult, VerdictPack } from "@nightcrew/shared";
import type { AppId, Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

/**
 * POST /v1/apps/:appId/validate/verdict - starts a verdict research run on the existing
 * app (the app row always pre-exists Validate, D1). No request-body schema is named by
 * the canonical contract; `brief` (the founder's Talk-to-Glim intake, collected
 * client-side - see `TalkToGlim.tsx`) is sent as the one reasonable payload this already-
 * canonical route needs to ground the research, never a new route.
 *
 * ASSUMED, flagged (no confirmed polling contract lands in packages/shared - ux spec §4.4's
 * own "Loading/researching" row cites the GET endpoint, not this POST, as the loading
 * state's touchpoint): this call starts the async research job; the caller does not treat
 * ITS OWN response as the final answer - it navigates to the verdict screen, which polls
 * {@link getVerdict} until a `VerdictPack` resolves. A `not_found` on that GET is read as
 * "still researching" (the same not_found-as-not-ready convention `src/api/knowledge.ts`/
 * AskCrew.tsx already established for the knowledge-doc readiness probe), never treated as
 * a real failure.
 */
export const requestVerdict = (
  appId: AppId,
  brief: string,
): Promise<Result<VerdictPack, ClientError>> =>
  apiPost(`/v1/apps/${appId}/validate/verdict`, VerdictPack, { brief });

/** GET /v1/apps/:appId/validate/verdict - reads the latest verdict; the poll target while
 * researching (ux spec §4.4). */
export const getVerdict = (appId: AppId): Promise<Result<VerdictPack, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/validate/verdict`, VerdictPack);

/** GET /v1/apps/:appId/validate/verdict/:verdictId - reads one specific past verdict. Not
 * exercised by this lane's own screens (Flow 1's live pack always reads the latest), kept
 * for completeness against the landed route table. */
export const getVerdictById = (
  appId: AppId,
  verdictId: string,
): Promise<Result<VerdictPack, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/validate/verdict/${encodeURIComponent(verdictId)}`, VerdictPack);

/** GET /v1/apps/:appId/validate/evidence/:claimId - the evidence drawer's per-claim detail.
 * Not exercised by `EvidenceDrawer.tsx` directly (a claim already carries its own evidence
 * inline on the pack response, and carries no stable id to key this route against - see
 * that component's own doc comment) - kept wired for completeness against the landed route
 * table, and for whichever lane resolves the claim-id gap and wants a true on-demand
 * per-claim fetch. */
export const EvidenceResponse = z.object({ evidence: z.array(EvidencePointer) });
export type EvidenceResponse = z.infer<typeof EvidenceResponse>;

export const getEvidence = (
  appId: AppId,
  claimId: string,
): Promise<Result<EvidenceResponse, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/validate/evidence/${encodeURIComponent(claimId)}`, EvidenceResponse);

/**
 * POST /v1/apps/:appId/validate/uploads (D77, the hardened gate) - multipart, the
 * platform's SECOND sensitive-upload path after Certify's data-room (slice4-certify-ux.md
 * §5). Declares `filename`/`mime`/`declaredSize` alongside the real file bytes (the
 * server compares the declared envelope against the actual upload - the same
 * `ValidateUploadRequest` shape, model/validate.ts:151-156), mirroring
 * `uploadCertificate`'s own FormData pattern (src/api/certify.ts). A rejected file is a
 * SUCCESSFUL response (`state: "rejected"`, model/validate.ts:171-189) - only a genuine
 * transfer/infra failure (network drop, 5xx) resolves to `isErr`; see
 * `UploadContextPanel.tsx` for how the two are told apart.
 */
export const uploadValidateContext = (
  appId: AppId,
  file: File,
): Promise<Result<ValidateUploadResult, ClientError>> => {
  const form = new FormData();
  form.set("filename", file.name);
  form.set("mime", file.type);
  form.set("declaredSize", String(file.size));
  form.set("file", file);
  return apiFetch(`/v1/apps/${appId}/validate/uploads`, ValidateUploadResult, {
    method: "POST",
    body: form,
  });
};
